import React from 'react';
import { 
  MapPin, 
  Clock, 
  User, 
  Phone,
  AlertTriangle,
  Flame,
  Heart,
  Shield,
  LifeBuoy,
  Car
} from 'lucide-react';
import { Emergency } from '../types';
import { StatusIndicator } from './StatusIndicator';

interface EmergencyCardProps {
  emergency: Emergency;
  assignedResponderName?: string;
  onUpdateStatus?: (id: string, status: Emergency['status']) => void;
  className?: string;
}

export const EmergencyCard: React.FC<EmergencyCardProps> = ({ 
  emergency, 
  assignedResponderName,
  onUpdateStatus,
  className = '' 
}) => {
  const getEmergencyIcon = () => {
    switch (emergency.type) {
      case 'fire':
        return { icon: Flame, color: 'text-red-600 bg-red-100' };
      case 'medical':
        return { icon: Heart, color: 'text-red-500 bg-red-50' };
      case 'police':
        return { icon: Shield, color: 'text-blue-600 bg-blue-100' };
      case 'rescue':
        return { icon: LifeBuoy, color: 'text-orange-600 bg-orange-100' };
      case 'accident':
        return { icon: Car, color: 'text-purple-600 bg-purple-100' };
      default:
        return { icon: AlertTriangle, color: 'text-gray-600 bg-gray-100' };
    }
  };

  const getSeverityColor = () => {
    switch (emergency.severity) {
      case 'critical':
        return 'border-l-red-600 bg-red-50';
      case 'high':
        return 'border-l-red-500 bg-red-25';
      case 'medium':
        return 'border-l-yellow-500 bg-yellow-25';
      case 'low':
        return 'border-l-blue-500 bg-blue-25';
    }
  };

  const iconConfig = getEmergencyIcon();
  const EmergencyIcon = iconConfig.icon;

  const formatTime = (date: Date) => {
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const getStatusActions = () => {
    if (!onUpdateStatus) return null;

    switch (emergency.status) {
      case 'assigned':
        return (
          <button
            onClick={() => onUpdateStatus(emergency.id, 'en-route')}
            className="px-3 py-1 bg-orange-600 text-white text-sm rounded-md hover:bg-orange-700 transition-colors"
          >
            En Route
          </button>
        );
      case 'en-route':
        return (
          <button
            onClick={() => onUpdateStatus(emergency.id, 'on-scene')}
            className="px-3 py-1 bg-purple-600 text-white text-sm rounded-md hover:bg-purple-700 transition-colors"
          >
            On Scene
          </button>
        );
      case 'on-scene':
        return (
          <button
            onClick={() => onUpdateStatus(emergency.id, 'resolved')}
            className="px-3 py-1 bg-green-600 text-white text-sm rounded-md hover:bg-green-700 transition-colors"
          >
            Resolve
          </button>
        );
      default:
        return null;
    }
  };

  return (
    <div className={`bg-white rounded-lg shadow-md border-l-4 ${getSeverityColor()} p-4 ${className}`}>
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-3">
          <div className={`w-10 h-10 rounded-full flex items-center justify-center ${iconConfig.color}`}>
            <EmergencyIcon className="w-5 h-5" />
          </div>
          <div>
            <h3 className="font-semibold text-gray-900 capitalize">
              {emergency.type} Emergency
            </h3>
            <p className="text-sm text-gray-600 capitalize">
              Severity: {emergency.severity}
            </p>
          </div>
        </div>
        <StatusIndicator status={emergency.status} severity={emergency.severity} />
      </div>

      <p className="text-gray-700 mb-3 text-sm leading-relaxed">
        {emergency.description}
      </p>

      <div className="space-y-2 text-sm text-gray-600 mb-4">
        <div className="flex items-center gap-2">
          <MapPin className="w-4 h-4 text-gray-400" />
          <span>
            {emergency.location.address || 
             `${emergency.location.latitude.toFixed(4)}, ${emergency.location.longitude.toFixed(4)}`}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <Clock className="w-4 h-4 text-gray-400" />
          <span>Reported at {formatTime(emergency.timestamp)}</span>
        </div>
        {assignedResponderName && (
          <div className="flex items-center gap-2">
            <User className="w-4 h-4 text-gray-400" />
            <span>Assigned to: {assignedResponderName}</span>
          </div>
        )}
        {emergency.estimatedArrival && (
          <div className="flex items-center gap-2">
            <Clock className="w-4 h-4 text-gray-400" />
            <span>ETA: {formatTime(emergency.estimatedArrival)}</span>
          </div>
        )}
      </div>

      {getStatusActions() && (
        <div className="flex justify-end pt-2 border-t">
          {getStatusActions()}
        </div>
      )}
    </div>
  );
};